import { ShopifyIcon, StripeIcon } from '@/components/icons';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { ArrowRight, CheckCircle } from 'iconoir-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { BillingLine } from './plan-choice-offer';

/** One short line under the billing title, a warning when the brand has something to redo. */
export type PlanChoiceFooterStatus = { text: string; tone?: 'muted' | 'warning' };

const BillingSummary = ({
  billing,
  freeBillingSummary,
  shopifyFreeTitle,
  status,
}: {
  billing: BillingLine | null;
  freeBillingSummary?: string;
  shopifyFreeTitle?: string;
  status?: PlanChoiceFooterStatus;
}) => {
  const { t } = useTranslation();
  if (!billing) return <Text flex="1">{t('onboarding.billing.paidUnavailable')}</Text>;

  return (
    <Flex flex="1" minW={0} align="center" gap="16px">
      <Flex
        flex="none"
        w="34px"
        h="34px"
        borderRadius="9px"
        bg="white"
        borderWidth="1px"
        borderColor="cardBorder"
        align="center"
        justify="center"
      >
        {billing === 'shopify' && <ShopifyIcon boxSize="19px" />}
        {billing === 'stripe' && <StripeIcon boxSize="19px" />}
        {billing === 'free' && (
          <CheckCircle width={18} height={18} color="var(--chakra-colors-green-600)" aria-hidden />
        )}
      </Flex>
      {billing === 'free' ? (
        <Text fontFamily="heading" fontSize="13.5px" fontWeight="600">
          {freeBillingSummary ?? t('planChoice.billing.free')}
        </Text>
      ) : (
        <Box minW={0}>
          <Flex align="center" gap="8px" flexWrap="wrap">
            <Text fontFamily="heading" fontSize="13.5px" fontWeight="600">
              {shopifyFreeTitle ?? t(`planChoice.billing.${billing}.title`)}
            </Text>
          </Flex>
          <Text
            data-testid="plan-choice-footer-status"
            fontSize="12.5px"
            color={status?.tone === 'warning' ? 'fg.warning' : 'inkMuted'}
            mt="1px"
          >
            {status?.text ??
              (shopifyFreeTitle ? freeBillingSummary : t(`planChoice.billing.${billing}.sub`))}
          </Text>
        </Box>
      )}
    </Flex>
  );
};

/**
 * How the chosen plan is paid, where it stands, and the one decision. A quieter
 * action (checking an approval already given) may sit beside the primary one.
 */
export const PlanChoiceFooter = ({
  billing,
  confirmLabel,
  onConfirm,
  disabled = false,
  loading = false,
  freeBillingSummary,
  shopifyFreeTitle,
  status,
  secondaryAction,
}: {
  billing: BillingLine | null;
  confirmLabel: string;
  onConfirm: () => void;
  disabled?: boolean;
  loading?: boolean;
  freeBillingSummary?: string;
  /** The Shopify Free plan with its price, in the currency of the plans on screen. */
  shopifyFreeTitle?: string;
  status?: PlanChoiceFooterStatus;
  secondaryAction?: ReactNode;
}) => {
  return (
    <Flex
      data-testid="plan-choice-footer"
      flexShrink={0}
      direction={{ base: 'column', md: 'row' }}
      align={{ base: 'stretch', md: 'center' }}
      gap="16px"
      px={{ base: '20px', md: '32px' }}
      py="16px"
      borderTopWidth="1px"
      borderColor="border.subtle"
      bg="background"
    >
      <BillingSummary
        billing={billing}
        freeBillingSummary={freeBillingSummary}
        shopifyFreeTitle={shopifyFreeTitle}
        status={status}
      />
      <Flex
        flex="none"
        direction={{ base: 'column-reverse', sm: 'row' }}
        align={{ base: 'stretch', sm: 'center' }}
        gap={{ base: '8px', sm: '16px' }}
      >
        {secondaryAction}
        <Button
          flex="none"
          colorPalette="primary"
          h="40px"
          px="18px"
          borderRadius="8px"
          fontWeight="500"
          onClick={onConfirm}
          disabled={disabled}
          loading={loading}
        >
          {confirmLabel}
          <ArrowRight width={16} height={16} aria-hidden />
        </Button>
      </Flex>
    </Flex>
  );
};
